import {
  FX_BREAKLINE_TYPES,
  FxBreaklineType,
  useFxWorkspaceContext,
} from '@mobile-mapping/providers';
import { BtnClasses, Button } from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import { Component, createMemo, createSignal, For } from 'solid-js';

export const BreaklinesExtractionSettings: Component = () => {
  const fxWorkspace = useFxWorkspaceContext();
  const [selectedBreaklineTypes, setSelectedBreaklineTypes] = createSignal<
    ReadonlySet<FxBreaklineType>
  >(new Set());

  const activeWorkspaceId = createMemo(() => fxWorkspace.activeWorkspaceId());

  const isBuilding = createMemo(() => {
    const workspaceId = activeWorkspaceId();
    return (
      workspaceId !== undefined && fxWorkspace.isBreaklinesBuilding(workspaceId)
    );
  });

  const canRunExtraction = createMemo(
    () =>
      selectedBreaklineTypes().size > 0 &&
      activeWorkspaceId() !== undefined &&
      !isBuilding()
  );

  const isBreaklineTypeSelected = (type: FxBreaklineType): boolean =>
    selectedBreaklineTypes().has(type);

  const toggleBreaklineType = (type: FxBreaklineType): void => {
    setSelectedBreaklineTypes((previous) => {
      const next = new Set(previous);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const handleRun = (): void => {
    const workspaceId = activeWorkspaceId();
    if (!canRunExtraction() || workspaceId === undefined) {
      return;
    }

    void fxWorkspace.runBreaklinesExtraction(workspaceId, [
      ...selectedBreaklineTypes(),
    ]);
  };

  return (
    <div
      class="flex w-full flex-col gap-4"
      data-testid="breaklines-extraction-settings"
    >
      <div class="flex flex-col gap-2">
        <p class="m-0 text-sm text-muted">
          {t('mobileMapping.fxWorkspace.extraction.breaklines.selectTypes')}
        </p>
        <div class="flex flex-col gap-2">
          <For each={FX_BREAKLINE_TYPES}>
            {(type) => (
              <div
                class="flex min-w-0 items-center gap-2"
                data-testid={`extraction-breakline-row-${type}`}
              >
                <modus-wc-checkbox
                  size="md"
                  class="shrink-0"
                  attr:data-testid={`extraction-breakline-${type}`}
                  attr:aria-label={t(
                    `mobileMapping.fxWorkspace.breaklines.${type}`
                  )}
                  value={isBreaklineTypeSelected(type)}
                  on:inputChange={() => toggleBreaklineType(type)}
                />
                <span
                  class="cursor-pointer truncate text-sm"
                  data-testid={`extraction-breakline-label-${type}`}
                  title={t(`mobileMapping.fxWorkspace.breaklines.${type}`)}
                  textContent={t(
                    `mobileMapping.fxWorkspace.breaklines.${type}`
                  )}
                  onClick={() => toggleBreaklineType(type)}
                />
              </div>
            )}
          </For>
        </div>
      </div>
      <Button
        color={BtnClasses.PRIMARY}
        icon="tc-icon-play"
        content={t('mobileMapping.fxWorkspace.extraction.run')}
        data-cy="extraction-workflow-run-button"
        class="self-start"
        disabled={!canRunExtraction()}
        onClick={handleRun}
      />
    </div>
  );
};
